import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { PaperPlaneTilt } from '@phosphor-icons/react';

const showSkippedToasts = (skipped) => {
  skipped.slice(0, 3).forEach((s) => {
    toast.error(`${s.claim_number || s.claim_id}: ${s.reason}`, { duration: 6000 });
  });
};

const ResubmitCard = ({ batch, hasRejection, onResubmitted }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const alreadyResubmitted = !!batch.resubmitted_as_batch_id;
  if (!hasRejection && !alreadyResubmitted) return null;

  const handleResubmit = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        `/odm-sftp/submissions/${encodeURIComponent(batch.batch_id)}/resubmit`,
      );
      const newBatchId = res.data.batch_id;
      const skippedCount = res.data.skipped_count || 0;
      const submittedCount = res.data.submitted_count || 0;
      toast.success(
        `Resubmitted ${submittedCount} claim${submittedCount === 1 ? '' : 's'} as ${newBatchId}` +
          (skippedCount ? ` · ${skippedCount} skipped` : ''),
      );
      if (onResubmitted) onResubmitted(newBatchId);
      setTimeout(() => navigate(`/odm/submissions/${newBatchId}`), 600);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(detail.message || 'Resubmit failed');
        if (Array.isArray(detail.skipped) && detail.skipped.length) {
          showSkippedToasts(detail.skipped);
        }
      } else {
        toast.error(detail || 'Resubmit failed');
      }
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <Card
      data-testid="odm-detail-resubmit-card"
      className={hasRejection ? 'border-amber-200' : ''}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PaperPlaneTilt size={16} className="text-amber-600" weight="duotone" />
          Resubmit this batch
        </CardTitle>
        <CardDescription className="text-xs">
          {alreadyResubmitted
            ? `This batch was already resubmitted as ${batch.resubmitted_as_batch_id}.`
            : "Re-validates each claim with the latest patient data and uploads a fresh 837P to ODM in one click. The new batch inherits this batch's notification list."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {alreadyResubmitted ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/odm/submissions/${batch.resubmitted_as_batch_id}`)}
            data-testid="odm-detail-go-to-resubmit-btn"
          >
            Open {batch.resubmitted_as_batch_id} →
          </Button>
        ) : confirming ? (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-700">
              Submit {batch.claim_count} claim{batch.claim_count === 1 ? '' : 's'} as a new batch?
            </span>
            <Button
              size="sm"
              onClick={handleResubmit}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="odm-detail-resubmit-confirm-btn"
            >
              {busy ? 'Resubmitting…' : 'Yes, resubmit now'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={() => setConfirming(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="odm-detail-resubmit-btn"
          >
            <PaperPlaneTilt size={14} className="mr-1" />
            Resubmit batch
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ResubmitCard;
